import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, getApiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, FileText, Sparkles, ChevronDown, ChevronUp, GitBranch, Lock, Layers, Save, HelpCircle, Paperclip, Download, X } from "lucide-react";
import { getAuditType } from "@/lib/auditTypes";
import { downloadProtectedFile } from "@/lib/assets";

const FLAGS = [
  { id: "conforme", label: "Conforme", bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-500" },
  { id: "parziale", label: "Parziale", bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-500" },
  { id: "non_conforme", label: "Non Conforme", bg: "bg-red-50", text: "text-red-700", border: "border-red-500" },
  { id: "na", label: "N/A", bg: "bg-slate-100", text: "text-slate-700", border: "border-slate-500" },
];

function FlagButton({ flag, active, onClick, disabled, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      className={`px-3 py-1.5 rounded-md text-xs font-semibold uppercase tracking-wider border transition-all duration-200 ${active ? `${flag.bg} ${flag.text} ${flag.border}` : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"} ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
    >
      {flag.label}
    </button>
  );
}

function ItemRow({ auditId, item, sectionId, onUpdate, onDelete, readOnly, initialAttachments }) {
  const [note, setNote] = useState(item.note || "");
  const [saving, setSaving] = useState(false);
  const [showNote, setShowNote] = useState(!!item.note);
  const [attachments, setAttachments] = useState(initialAttachments || []);
  const [showAtt, setShowAtt] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { setAttachments(initialAttachments || []); }, [initialAttachments]);

  const setFlag = async (flagId) => {
    if (readOnly) return;
    try {
      await api.patch(`/audits/${auditId}/items/${item.id}`, { flag: flagId });
      onUpdate(sectionId, item.id, { flag: flagId });
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore salvataggio"); }
  };

  const saveNote = async () => {
    if (readOnly) return;
    setSaving(true);
    try {
      await api.patch(`/audits/${auditId}/items/${item.id}`, { note });
      onUpdate(sectionId, item.id, { note });
      toast.success("Nota salvata");
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  const uploadFile = async (fileObj) => {
    if (!fileObj || readOnly) return;
    if (fileObj.size > 20 * 1024 * 1024) { toast.error("File troppo grande (max 20MB)"); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", fileObj);
      const { data } = await api.post(`/audits/${auditId}/items/${item.id}/attachments`, form, { headers: { "Content-Type": "multipart/form-data" } });
      setAttachments((a) => [data, ...a]);
      setShowAtt(true);
      toast.success("File allegato");
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore upload"); }
    finally { setUploading(false); }
  };

  const removeAttachment = async (att) => {
    if (readOnly) return;
    if (!window.confirm(`Rimuovere l'allegato "${att.original_filename}"?`)) return;
    try {
      await api.delete(`/attachments/${att.id}`);
      setAttachments((a) => a.filter((x) => x.id !== att.id));
      toast.success("Allegato rimosso");
    } catch { toast.error("Impossibile rimuovere"); }
  };

  const downloadAttachment = (att) => {
    downloadProtectedFile(`/attachments/${att.id}/download`, att.original_filename).catch((err) => {
      toast.error(getApiErrorMessage(err, "Download non riuscito"));
    });
  };

  const fmtSize = (b) => {
    if (!b) return "";
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div data-testid={`item-row-${item.id}`} className="border-t border-slate-100 py-4 first:border-t-0">
      <div className="flex items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="font-mono-plex text-[11px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">{item.code}</span>
            <span className="text-[10px] uppercase tracking-wider text-slate-400">Peso {item.weight}/5</span>
            {item.custom && <span className="text-[10px] uppercase tracking-wider text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">Custom</span>}
            {attachments.length > 0 && (
              <span className="text-[10px] uppercase tracking-wider text-indigo-700 bg-indigo-50 px-1.5 py-0.5 rounded inline-flex items-center gap-1">
                <Paperclip className="h-2.5 w-2.5" /> {attachments.length}
              </span>
            )}
          </div>
          <div className="font-medium text-slate-900 leading-snug">{item.title}</div>
          {item.description && <div className="text-sm text-slate-600 mt-1 leading-relaxed">{item.description}</div>}
          {item.reference && <div className="text-[11px] text-slate-400 mt-1 font-mono-plex">{item.reference}</div>}
        </div>
        {item.custom && !readOnly && (
          <button onClick={() => onDelete(item.id)} data-testid={`delete-item-${item.id}`} className="p-2 text-slate-400 hover:text-red-600 rounded-md transition-colors duration-200">
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {FLAGS.map((f) => (
          <FlagButton key={f.id} flag={f} active={item.flag === f.id} onClick={() => setFlag(f.id)} disabled={readOnly} testid={`flag-${item.id}-${f.id}`} />
        ))}
        <button onClick={() => setShowNote(!showNote)} className="ml-2 text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1 transition-colors duration-200" data-testid={`toggle-note-${item.id}`}>
          Note {showNote ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </button>
        <button onClick={() => setShowAtt(!showAtt)} className="text-xs text-slate-500 hover:text-indigo-700 flex items-center gap-1 transition-colors duration-200" data-testid={`toggle-att-${item.id}`}>
          <Paperclip className="h-3 w-3" /> Evidenze {attachments.length > 0 && <span className="font-mono-plex text-indigo-700">({attachments.length})</span>} {showAtt ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </button>
      </div>
      {showNote && (
        <div className="mt-3">
          <Textarea data-testid={`note-input-${item.id}`} value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder={readOnly ? "Sola lettura" : "Osservazioni, evidenze, riferimenti…"} disabled={readOnly} className="text-sm" />
          {!readOnly && (
            <div className="mt-2 flex justify-end">
              <Button type="button" size="sm" variant="outline" data-testid={`save-note-${item.id}`} onClick={saveNote} disabled={saving} className="h-8 text-xs">{saving ? "Salvataggio…" : "Salva nota"}</Button>
            </div>
          )}
        </div>
      )}
      {showAtt && (
        <div className="mt-3 bg-slate-50 border border-slate-200 rounded-md p-3">
          {attachments.length === 0 && <div className="text-xs text-slate-500 mb-2">Nessuna evidenza documentale allegata.</div>}
          {attachments.length > 0 && (
            <ul className="space-y-1.5 mb-3">
              {attachments.map((att) => (
                <li key={att.id} data-testid={`attachment-${att.id}`} className="flex items-center gap-2 bg-white border border-slate-200 rounded-md px-2.5 py-1.5">
                  <FileText className="h-3.5 w-3.5 text-indigo-600 shrink-0" />
                  <button onClick={() => downloadAttachment(att)} className="flex-1 text-left text-xs text-slate-800 hover:text-indigo-700 truncate transition-colors" data-testid={`download-att-${att.id}`} title={att.original_filename}>
                    {att.original_filename}
                  </button>
                  <span className="text-[10px] text-slate-400 font-mono-plex">{fmtSize(att.size)}</span>
                  <button onClick={() => downloadAttachment(att)} className="p-1 text-slate-400 hover:text-indigo-700" title="Scarica" data-testid={`download-btn-${att.id}`}>
                    <Download className="h-3.5 w-3.5" />
                  </button>
                  {!readOnly && (
                    <button onClick={() => removeAttachment(att)} className="p-1 text-slate-400 hover:text-red-600" title="Elimina" data-testid={`remove-att-${att.id}`}>
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
          {!readOnly && (
            <label className="inline-flex items-center gap-2 cursor-pointer text-xs text-slate-700 hover:text-indigo-700 transition-colors">
              <input
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.webp,.txt,.csv"
                data-testid={`upload-input-${item.id}`}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile(f); e.target.value = ""; }}
                className="hidden"
                disabled={uploading}
              />
              <span className={`px-2.5 py-1.5 border-2 border-dashed border-slate-300 rounded-md ${uploading ? "opacity-50" : "hover:border-indigo-400 hover:bg-indigo-50"} transition-colors inline-flex items-center gap-1.5`}>
                <Paperclip className="h-3 w-3" /> {uploading ? "Caricamento…" : "Allega evidenza (PDF, Word, Excel, immagini)"}
              </span>
            </label>
          )}
        </div>
      )}
    </div>
  );
}

export default function AuditDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [audit, setAudit] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [attachmentsByItem, setAttachmentsByItem] = useState({});
  const [generating, setGenerating] = useState(false);
  const [extraOpen, setExtraOpen] = useState(false);
  const [reportPrompt, setReportPrompt] = useState("");
  const [extraInstructions, setExtraInstructions] = useState("");
  const [duplicating, setDuplicating] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ section_id: "", title: "", description: "", weight: 3, reference: "" });
  const [addFwOpen, setAddFwOpen] = useState(false);
  const [addFwType, setAddFwType] = useState("");
  const [saveTplOpen, setSaveTplOpen] = useState(false);
  const [saveTplForm, setSaveTplForm] = useState({ name: "", description: "", keep_notes: false });

  const load = useCallback(() => api.get(`/audits/${id}`).then((r) => setAudit(r.data)).catch(() => toast.error("Impossibile caricare l'audit")), [id]);
  const loadAttachments = useCallback(() => api.get(`/audits/${id}/attachments`).then((r) => setAttachmentsByItem(r.data || {})).catch((err) => console.warn("Attachments load failed:", err?.response?.data?.detail || err.message)), [id]);
  useEffect(() => {
    load();
    loadAttachments();
    api.get("/settings/ai").then((r) => setReportPrompt(r.data.custom_prompt || "")).catch(() => {});
    api.get("/templates").then((r) => setTemplates(r.data)).catch((err) => console.warn("Templates load failed:", err?.response?.data?.detail || err.message));
  }, [id, load, loadAttachments]);

  const readOnly = !!audit?.frozen;

  const updateItem = (section_id, item_id, patch) => {
    setAudit((a) => {
      if (!a) return a;
      const sections = a.sections.map((s) => s.id !== section_id ? s : ({
        ...s, items: s.items.map((it) => it.id === item_id ? { ...it, ...patch } : it)
      }));
      return { ...a, sections };
    });
    api.get(`/audits/${id}`).then((r) => setAudit(r.data)).catch((err) => console.warn("Audit refresh failed:", err?.response?.data?.detail || err.message));
  };

  const deleteItem = async (item_id) => {
    try { await api.delete(`/audits/${id}/items/${item_id}`); toast.success("Elemento rimosso"); load(); }
    catch { toast.error("Impossibile eliminare"); }
  };

  const addItem = async (e) => {
    e.preventDefault();
    if (!addForm.section_id || !addForm.title) { toast.error("Seleziona sezione e titolo"); return; }
    try {
      await api.post(`/audits/${id}/items`, addForm);
      toast.success("Requisito aggiunto");
      setAddOpen(false);
      setAddForm({ section_id: "", title: "", description: "", weight: 3, reference: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore aggiunta"); }
  };

  const addFramework = async () => {
    if (!addFwType) { toast.error("Seleziona un framework"); return; }
    try {
      await api.post(`/audits/${id}/add-framework`, { type: addFwType });
      toast.success("Framework aggiunto");
      setAddFwOpen(false);
      setAddFwType("");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const duplicateAudit = async () => {
    if (!window.confirm("Vuoi creare una nuova revisione dell'audit? La versione attuale verrà congelata come storico.")) return;
    setDuplicating(true);
    try {
      const { data } = await api.post(`/audits/${id}/duplicate`);
      toast.success(`Revisione v${data.version} creata`);
      navigate(`/audit/${data.id}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore duplicazione"); }
    finally { setDuplicating(false); }
  };

  const saveAsTemplate = async () => {
    if (!saveTplForm.name.trim()) { toast.error("Inserisci un nome"); return; }
    try {
      await api.post(`/audits/${id}/save-as-template`, saveTplForm);
      toast.success("Template creato dalla checklist");
      setSaveTplOpen(false);
      setSaveTplForm({ name: "", description: "", keep_notes: false });
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const stats = useMemo(() => {
    if (!audit) return { total: 0, done: 0, byFlag: {} };
    const byFlag = { conforme: 0, non_conforme: 0, parziale: 0, na: 0, none: 0 };
    let total = 0;
    audit.sections.forEach((s) => s.items.forEach((it) => { total++; byFlag[it.flag || "none"]++; }));
    return { total, done: total - byFlag.none, byFlag };
  }, [audit]);

  const generate = async () => {
    if (readOnly) return;
    setGenerating(true);
    try {
      const { data } = await api.post(`/audits/${id}/generate-report`, { override_prompt: reportPrompt || null, extra_instructions: extraInstructions || null });
      const jobId = data.job_id;
      // Poll every 4s up to ~10 min for large chunked reports
      const maxAttempts = 150;
      for (let i = 0; i < maxAttempts; i++) {
        await new Promise((r) => setTimeout(r, 4000));
        try {
          const { data: job } = await api.get(`/generation-jobs/${jobId}`);
          if (job.status === "done") {
            toast.success("Relazione generata");
            setExtraOpen(false);
            setExtraInstructions("");
            navigate(`/audit/${id}/relazione`);
            return;
          }
          if (job.status === "error") {
            toast.error(job.error || "Errore generazione");
            return;
          }
          if (i > 0 && i % 8 === 0) toast.message(`Generazione in corso… (${Math.round(i*4/60)} min)`);
        } catch (err) { console.warn("Report poll transient error:", err.message); }
      }
      toast.error("Timeout generazione. Ricarica la pagina fra qualche minuto per vedere la relazione.");
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore generazione"); }
    finally { setGenerating(false); }
  };

  const openGenerate = () => {
    if (readOnly) return;
    if (stats.done < stats.total && !window.confirm(`Hai valutato ${stats.done}/${stats.total} controlli. Procedere comunque con la generazione della relazione?`)) return;
    setExtraOpen(true);
  };

  if (!audit) return <div className="p-10 text-slate-500">Caricamento…</div>;
  const progress = stats.total ? Math.round((stats.done / stats.total) * 100) : 0;
  const availableFrameworks = templates.filter((t) => !audit.types.includes(t.type));

  // Group sections by framework for rendering
  const sectionsByFramework = audit.sections.reduce((acc, s) => {
    const fw = s.framework || "unknown";
    (acc[fw] ||= []).push(s);
    return acc;
  }, {});

  return (
    <div className="pb-16">
      <div className="sticky top-0 z-20 bg-white/85 backdrop-blur-md border-b border-slate-200">
         <div className="p-4 md:px-10 max-w-6xl mx-auto">
           <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <Link to="/audit" className="text-slate-500 hover:text-slate-900 transition-colors duration-200"><ArrowLeft className="h-5 w-5" /></Link>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  {audit.types.map((t) => {
                    const m = getAuditType(t);
                    return <span key={t} className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${m.badge_bg} ${m.badge_text}`}>{m.label}</span>;
                  })}
                  <span className="text-[10px] uppercase tracking-wider text-slate-500 font-mono-plex" data-testid="audit-version">v{audit.version || 1}</span>
                  {readOnly && (
                    <span className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-slate-900 text-white inline-flex items-center gap-1">
                      <Lock className="h-2.5 w-2.5" /> Storico
                    </span>
                  )}
                </div>
                <h1 className="font-display font-black text-xl md:text-2xl truncate" data-testid="audit-title">{audit.title}</h1>
                <div className="text-xs text-slate-500 mt-0.5">{audit.company_name}</div>
              </div>
            </div>
             <div className="flex items-center gap-2 flex-wrap w-full md:w-auto md:justify-end">
               <div className="text-left md:text-right pr-2 mr-auto md:mr-0">
                <div className="font-display font-black text-2xl text-slate-900" data-testid="audit-score">{audit.score}%</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Score compliance</div>
              </div>
              {readOnly ? (
                <Button onClick={duplicateAudit} disabled={duplicating} data-testid="new-revision-btn" className="h-10 bg-slate-900 hover:bg-slate-800">
                  <GitBranch className="h-4 w-4" /> {duplicating ? "…" : "Nuova revisione"}
                </Button>
              ) : (
                <>
                   <Button onClick={openGenerate} disabled={generating} data-testid="generate-report-btn" className="h-10 bg-slate-900 hover:bg-slate-800">
                     <Sparkles className="h-4 w-4" /> {generating ? "…" : (audit.report ? "Rigenera" : "Genera relazione")}
                   </Button>
                  <Button onClick={duplicateAudit} disabled={duplicating} variant="outline" data-testid="new-revision-btn" className="h-10">
                    <GitBranch className="h-4 w-4" /> Nuova rev.
                  </Button>
                </>
              )}
              {audit.report && (
                <Link to={`/audit/${id}/relazione`}>
                  <Button variant="outline" className="h-10" data-testid="view-report-btn"><FileText className="h-4 w-4" /> Relazione</Button>
                </Link>
              )}
            </div>
          </div>
           <div className="mt-4 flex items-center gap-4 text-xs text-slate-500">
            <div className="flex-1 max-w-md">
              <div className="flex items-center justify-between mb-1"><span>Compilazione</span><span className="font-mono-plex">{stats.done}/{stats.total}</span></div>
              <Progress value={progress} className="h-1.5" />
            </div>
            <div className="hidden md:flex items-center gap-3 text-[11px] font-mono-plex">
              <span className="text-emerald-600">C {stats.byFlag.conforme}</span>
              <span className="text-amber-600">P {stats.byFlag.parziale}</span>
              <span className="text-red-600">NC {stats.byFlag.non_conforme}</span>
              <span className="text-slate-500">NA {stats.byFlag.na}</span>
            </div>
          </div>
        </div>
      </div>

       <div className="p-4 md:p-10 max-w-6xl mx-auto">
         <div className="mb-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
           <div className="rounded-xl border border-slate-200 bg-white p-4"><div className="text-[10px] uppercase tracking-wider text-slate-500">Controlli totali</div><div className="mt-1 font-display text-2xl font-black text-slate-900">{stats.total}</div></div>
           <div className="rounded-xl border border-slate-200 bg-white p-4"><div className="text-[10px] uppercase tracking-wider text-slate-500">Valutati</div><div className="mt-1 font-display text-2xl font-black text-slate-900">{stats.done}<span className="text-sm font-normal text-slate-400"> / {stats.total}</span></div></div>
           <div className="rounded-xl border border-emerald-100 bg-emerald-50/50 p-4"><div className="text-[10px] uppercase tracking-wider text-emerald-700">Conformi</div><div className="mt-1 font-display text-2xl font-black text-emerald-700">{stats.byFlag.conforme}</div></div>
           <div className="rounded-xl border border-amber-100 bg-amber-50/50 p-4"><div className="text-[10px] uppercase tracking-wider text-amber-700">Avanzamento</div><div className="mt-1 font-display text-2xl font-black text-amber-700">{progress}%</div></div>
         </div>
        {audit.by_framework && Object.keys(audit.by_framework).length > 1 && (
          <div className="mb-6 grid grid-cols-2 md:grid-cols-5 gap-3">
            {Object.entries(audit.by_framework).map(([fw, st]) => {
              const m = getAuditType(fw);
              return (
                <div key={fw} className="bg-white border border-slate-200 rounded-md p-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${m.badge_bg} ${m.badge_text}`}>{m.label}</span>
                  </div>
                  <div className="font-display font-black text-xl text-slate-900" data-testid={`fw-score-${fw}`}>{st.score}%</div>
                  <div className="text-[10px] text-slate-500 font-mono-plex">{st.evaluated}/{st.total_items}</div>
                </div>
              );
            })}
          </div>
        )}

        {!readOnly && (
          <div className="flex justify-end mb-4 gap-2 flex-wrap">
            {availableFrameworks.length > 0 && (
              <Dialog open={addFwOpen} onOpenChange={setAddFwOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="h-9" data-testid="add-framework-btn"><Layers className="h-4 w-4" /> Aggiungi framework</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle className="font-display font-black text-xl">Aggiungi framework</DialogTitle></DialogHeader>
                  <div className="space-y-4">
                    <p className="text-sm text-slate-500">Aggiungi un framework in più a questo audit. Le nuove sezioni verranno accodate a quelle attuali.</p>
                    <Select value={addFwType} onValueChange={setAddFwType}>
                      <SelectTrigger data-testid="add-framework-select" className="h-11"><SelectValue placeholder="Seleziona framework…" /></SelectTrigger>
                      <SelectContent>
                        {availableFrameworks.map((t) => (
                          <SelectItem key={t.type} value={t.type}>{t.name} · {t.items_count} controlli</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <DialogFooter>
                    <Button onClick={addFramework} data-testid="add-framework-submit" className="bg-slate-900 hover:bg-slate-800">Aggiungi</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
            <Dialog open={saveTplOpen} onOpenChange={setSaveTplOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="h-9" data-testid="save-as-template-btn"><Save className="h-4 w-4" /> Salva come template</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-display font-black text-xl">Salva checklist come template</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <p className="text-sm text-slate-500">Trasforma la checklist di questo audit (inclusi i requisiti custom aggiunti) in un template riutilizzabile.</p>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-600">Nome template</Label>
                    <Input data-testid="save-tpl-name" value={saveTplForm.name} onChange={(e) => setSaveTplForm({ ...saveTplForm, name: e.target.value })} className="mt-2 h-11" placeholder="Es. Baseline cliente Rossi SpA" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-600">Descrizione</Label>
                    <Textarea data-testid="save-tpl-desc" value={saveTplForm.description} onChange={(e) => setSaveTplForm({ ...saveTplForm, description: e.target.value })} rows={2} className="mt-2" />
                  </div>
                  <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
                    <input type="checkbox" data-testid="save-tpl-keep-notes" checked={saveTplForm.keep_notes} onChange={(e) => setSaveTplForm({ ...saveTplForm, keep_notes: e.target.checked })} className="mt-1" />
                    <span>Mantieni le note dell'auditor come parte della descrizione dei requisiti</span>
                  </label>
                </div>
                <DialogFooter>
                  <Button onClick={saveAsTemplate} data-testid="save-tpl-submit" className="bg-slate-900 hover:bg-slate-800">Salva template</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="h-9" data-testid="add-custom-item-btn"><Plus className="h-4 w-4" /> Aggiungi requisito</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-display font-black text-xl">Nuovo requisito personalizzato</DialogTitle></DialogHeader>
                <form onSubmit={addItem} className="space-y-4">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-600">Sezione</Label>
                    <Select value={addForm.section_id} onValueChange={(v) => setAddForm({ ...addForm, section_id: v })}>
                      <SelectTrigger data-testid="add-item-section-select" className="mt-2 h-11"><SelectValue placeholder="Seleziona sezione…" /></SelectTrigger>
                      <SelectContent>
                        {audit.sections.map((s) => (<SelectItem key={s.id} value={s.id}>[{getAuditType(s.framework).label}] {s.title}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-600">Titolo requisito</Label>
                    <Input data-testid="add-item-title-input" required value={addForm.title} onChange={(e) => setAddForm({ ...addForm, title: e.target.value })} className="mt-2 h-11" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-600">Descrizione</Label>
                    <Textarea data-testid="add-item-desc-input" value={addForm.description} onChange={(e) => setAddForm({ ...addForm, description: e.target.value })} className="mt-2" rows={2} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-slate-600">Peso (1-5)</Label>
                      <Input type="number" min={1} max={5} data-testid="add-item-weight-input" value={addForm.weight} onChange={(e) => setAddForm({ ...addForm, weight: Number(e.target.value) })} className="mt-2 h-11" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-slate-600">Riferimento normativo</Label>
                      <Input data-testid="add-item-ref-input" value={addForm.reference} onChange={(e) => setAddForm({ ...addForm, reference: e.target.value })} className="mt-2 h-11" />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="submit" data-testid="add-item-submit" className="bg-slate-900 hover:bg-slate-800">Aggiungi</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        )}

        <div className="space-y-8">
          {audit.types.map((fw) => {
            const sections = sectionsByFramework[fw] || [];
            const meta = getAuditType(fw);
            const fwStats = audit.by_framework?.[fw];
            return (
              <div key={fw} data-testid={`framework-block-${fw}`}>
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-200">
                  <span className={`text-[11px] uppercase tracking-wider font-semibold px-2 py-1 rounded ${meta.badge_bg} ${meta.badge_text}`}>{meta.label}</span>
                  <span className="text-xs text-slate-500">{meta.short_ref}</span>
                  {fwStats && <span className="ml-auto font-mono-plex text-sm text-slate-900 font-semibold">{fwStats.score}%</span>}
                </div>
                <div className="space-y-4">
                  {sections.map((section) => {
                    const sDone = section.items.filter((i) => i.flag).length;
                    const sPct = section.items.length ? Math.round((sDone / section.items.length) * 100) : 0;
                    return (
                       <div key={section.id} data-testid={`section-${section.id}`} className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
                         <div className="px-4 md:px-6 py-4 border-b border-slate-100 flex items-center justify-between gap-4">
                           <div className="min-w-0">
                             <div className="mb-1 flex flex-wrap items-center gap-1.5">
                               {section.template_name && <span className="rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-blue-700">{section.template_name}</span>}
                               {section.industry_tag && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-600">TAG: {section.industry_tag}</span>}
                             </div>
                             <h2 className="font-display font-bold text-lg">{section.title}</h2>
                            <div className="text-xs text-slate-500 mt-0.5 font-mono-plex">{sDone}/{section.items.length} valutati</div>
                          </div>
                          <div className="w-32">
                            <Progress value={sPct} className="h-1.5" />
                          </div>
                        </div>
                         <div className="px-4 md:px-6 py-2">
                          {section.items.map((it) => (
                            <ItemRow key={it.id} auditId={id} item={it} sectionId={section.id} onUpdate={updateItem} onDelete={deleteItem} readOnly={readOnly} initialAttachments={attachmentsByItem[it.id] || []} />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

         {!readOnly && (
           <div className="mt-8 flex justify-end gap-3 flex-wrap">
             <Button onClick={openGenerate} disabled={generating} data-testid="generate-report-bottom-btn" className="h-11 bg-slate-900 hover:bg-slate-800 px-6">
               <Sparkles className="h-4 w-4" /> {generating ? "Generazione in corso…" : "Genera relazione con AI"}
             </Button>
           </div>
         )}

         <Dialog open={extraOpen} onOpenChange={setExtraOpen}>
           <DialogContent className="max-w-xl">
             <DialogHeader><DialogTitle className="font-display font-black text-2xl">Genera relazione</DialogTitle></DialogHeader>
             <div className="space-y-4">
               <p className="text-sm text-slate-600 leading-relaxed">
                 Il contenuto dell'audit verrà inviato al modello configurato in <Link to="/impostazioni/ai" className="text-indigo-700 hover:underline">Impostazioni AI</Link>.
               </p>
               <div>
                 <Label className="text-xs uppercase tracking-wider text-slate-600">Istruzioni per il verbale</Label>
                 <Textarea
                   data-testid="report-prompt-input"
                   value={reportPrompt}
                   onChange={(e) => setReportPrompt(e.target.value)}
                   rows={10}
                   className="mt-2 font-mono-plex text-xs"
                   placeholder="Istruzioni per il modello..."
                 />
                 <div className="mt-1.5 text-[11px] text-slate-500">Queste istruzioni verranno salvate automaticamente come nuovo default.</div>
               </div>
               <div>
                 <Label className="text-xs uppercase tracking-wider text-slate-600">Istruzioni aggiuntive (opzionale)</Label>
                 <Textarea
                   data-testid="report-extra-input"
                   value={extraInstructions}
                   onChange={(e) => setExtraInstructions(e.target.value)}
                   rows={6}
                   className="mt-2"
                   placeholder="Es. focalizza il verbale sulla mancanza di formazione. Aggiungi una sezione dedicata al DPO. Usa uno stile più tecnico…"
                 />
                 <div className="mt-1.5 text-[11px] text-slate-500">{extraInstructions.length} caratteri</div>
               </div>
               <div className="text-[11px] text-slate-500 bg-indigo-50 border border-indigo-100 rounded-md p-3 leading-relaxed">
                 La relazione verrà salvata nell’audit e potrà essere rigenerata. Per audit grandi la generazione può richiedere più chiamate al modello.
               </div>
             </div>
             <DialogFooter>
               <Button onClick={() => setExtraOpen(false)} variant="outline">Annulla</Button>
               <Button onClick={generate} disabled={generating} data-testid="report-generate-submit" className="bg-slate-900 hover:bg-slate-800">
                 <Sparkles className="h-4 w-4" /> {generating ? "Generazione…" : "Genera relazione"}
               </Button>
             </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
